"use client";

import React, { useState, useMemo } from "react";
import { MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const BUSINESS_PHONE = "393402498369";
function CategorySidebarConsumer({
  selectedCategory,
  onSelectCategory
}: {
  selectedCategory: string;
  onSelectCategory: (val: string) => void;
}) {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <div className="space-y-2">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-muted/50" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-destructive">Errore caricamento categorie.</div>;
  }
  const handleCategoryChange = (val: string) => {
    console.log("Category selected:", val);
    onSelectCategory(val);
  };
  return <Select value={selectedCategory} onValueChange={handleCategoryChange}>
      <SelectTrigger className="w-full bg-card font-medium text-foreground">
        <SelectValue placeholder="Seleziona categoria" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tutti i Dolci</SelectItem>
        {terms.map((term, i) => <SelectItem key={term.id} data-index={i} value={String(term.id)}>
            {term.name}
          </SelectItem>)}
      </SelectContent>
    </Select>;
}
function CategorySidebar({
  selectedCategory,
  onSelectCategory
}: {
  selectedCategory: string;
  onSelectCategory: (val: string) => void;
}) {
  const wp_term_query = useMemo(() => new WP_Term_Query({
    taxonomy: "product_cat",
    hide_empty: true
  }), []);
  return <div className="w-full lg:w-64 lg:shrink-0">
      <h3 className="mb-4 font-serif text-xl font-semibold text-foreground lg:mb-6">Categorie</h3>
      <EcommerceTermsProvider wp_term_query={wp_term_query}>
        <CategorySidebarConsumer selectedCategory={selectedCategory} onSelectCategory={onSelectCategory} />
      </EcommerceTermsProvider>
    </div>;
}
function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[400px] animate-pulse rounded-xl bg-muted/30" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">Impossibile caricare i dolci al momento.</p>
        <Button onClick={() => refetch()} variant="outline">
          Riprova
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg text-muted-foreground">Nessun dolce trovato in questa categoria.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const whatsappMessage = encodeURIComponent(`Salve, vorrei ordinare o chiedere informazioni su: ${product.title}`);
      const whatsappLink = `https://wa.me/${BUSINESS_PHONE}?text=${whatsappMessage}`;
      return <Card key={product.id} data-index={i} className="group overflow-hidden border-border/50 bg-card transition-all hover:shadow-md">
            <div className="relative aspect-square overflow-hidden bg-muted">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-sm">Immagine non disponibile</span>
                </div>}
            </div>
            
            <CardHeader className="p-5 pb-2">
              <CardTitle className="font-serif text-xl font-medium text-foreground">
                {product.title}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-5 py-2">
              {product.short_description && <div className="mt-2 line-clamp-2 text-sm text-muted-foreground" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} />}
            </CardContent>

            <CardFooter className="p-5 pt-4">
              <Button asChild className="w-full gap-2 bg-[#25D366] text-white hover:bg-[#128C7E] hover:text-white">
                <Link to={whatsappLink} newTab>
                  <MessageCircle className="h-4 w-4" />
                  Ordina su WhatsApp
                </Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
function ProductGrid({
  categoryId
}: {
  categoryId: string;
}) {
  const wp_query = useMemo(() => {
    const params: any = {
      post_type: "product",
      per_page: 9,
      status: "publish",
      orderby: "menu_order",
      order: "ASC"
    };
    if (categoryId && categoryId !== "all") {
      params.product_cat = parseInt(categoryId, 10);
    }
    return new WP_Query(params);
  }, [categoryId]);
  return <div className="flex-1">
      <EcommerceProductsProvider wp_query={wp_query}>
        <ProductGridConsumer />
      </EcommerceProductsProvider>
    </div>;
}
export default function TuttiINostriDolci() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  return <section id="tutti-i-nostri-dolci" className="bg-background py-16 lg:py-24">
      <div id="tutti-i-nostri-dolci" className="container mx-auto px-4">
        <div className="mb-10 text-center md:mb-16">
          <h2 className="mb-4 font-serif text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Tutti i Nostri Dolci
          </h2>
          <p className="mx-auto max-w-2xl text-muted-foreground md:text-lg">
            Esplora la nostra selezione di autentica pasticceria siciliana. 
            Dai classici cannoli alle torte personalizzate, ogni dolce è un'opera d'arte.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
          
          <CategorySidebar selectedCategory={selectedCategory} onSelectCategory={setSelectedCategory} />

          
          <ProductGrid key={selectedCategory} categoryId={selectedCategory} />
        </div>
      </div>
    </section>;
}